import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface MilkyLiquidProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** Height of the liquid column in metres. */
  height?: number;
  /** Radius of the liquid column. Should match the container's inner radius. */
  radius?: number;
  /** 0 = transparent clear liquid, 1 = fully opaque milky white. */
  milkyAmount?: number;
}

/**
 * Generic liquid column whose cloudiness can be animated from clear to
 * milky-white. Used in Part A of Activity 3.18 for limewater turning
 * milky as CO₂ flows through it.
 *
 * The column is a single cylinder mesh whose `opacity`, `transmission`
 * and base colour are lerped per frame by `milkyAmount`.
 */
export function MilkyLiquid({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  height = 0.18,
  radius = 0.048,
  milkyAmount = 0,
}: MilkyLiquidProps) {
  const matRef = useRef<THREE.MeshPhysicalMaterial>(null);

  useFrame(() => {
    if (!matRef.current) return;
    const t = Math.max(0, Math.min(1, milkyAmount));
    // When clear: high transmission, low opacity, faint blue tint.
    // When milky: low transmission, high opacity, white.
    matRef.current.opacity = 0.45 + 0.50 * t;
    matRef.current.transmission = 0.95 * (1 - t);
    matRef.current.roughness = 0.05 + 0.55 * t;
    const r = 0.92 + 0.06 * t;
    const g = 0.97 + 0.02 * t;
    const b = 1.0 - 0.05 * t;
    matRef.current.color.setRGB(r, g, b);
  });

  return (
    <group position={position} rotation={rotation} scale={scale}>
      <mesh position={[0, height / 2, 0]}>
        <cylinderGeometry args={[radius, radius, height, 32]} />
        <meshPhysicalMaterial
          ref={matRef}
          color="#ecf6fb"
          transparent
          opacity={0.45}
          transmission={0.95}
          roughness={0.05}
          ior={1.33}
          thickness={0.02}
          attenuationColor="#e3f3ff"
          attenuationDistance={1.0}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>
      {/* Subtle meniscus line at the top */}
      <mesh position={[0, height + 0.001, 0]}>
        <cylinderGeometry args={[radius * 0.98, radius * 0.99, 0.002, 32]} />
        <meshPhysicalMaterial
          color="#f5fbff"
          transparent
          opacity={0.6}
          roughness={0.1}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

export default MilkyLiquid;
